// Title, description, canonical and hreflang builders for every public page.
// Pure functions (unit-tested in meta.test.js). The backend crawler pages
// (backend/utils/seo.py: product_title, product_description, …) build the
// same strings for bots that do not run JavaScript: keep the two in step.

export const SITE_URL = 'https://anum360.ae';
export const SITE_NAME = 'ANUM 360 Gallery';
export const TITLE_MAX = 60;
export const DESCRIPTION_MAX = 155;

// Language in the URL: English is the default (no parameter), Arabic is
// `?lang=ar`. The LanguageProvider reads it on load, so the same URL always
// renders the same language for crawlers and people.
export const LANG_PARAM = 'lang';

const ELLIPSIS = '…';

const squash = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

/**
 * Shortens `text` to at most `max` characters, at a word boundary when one
 * is close, ending with an ellipsis. Never returns more than `max`.
 */
export const clampText = (value, max) => {
  const text = squash(value);
  if (text.length <= max) return text;
  if (max <= 1) return text.slice(0, max);
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  const base = space >= Math.floor(max * 0.6) ? cut.slice(0, space) : cut;
  return `${base.replace(/[\s,;:.–—|-]+$/, '')}${ELLIPSIS}`;
};

const SUFFIX = { en: `| ${SITE_NAME}`, ar: `| ${SITE_NAME}` };
const SUFFIX_CITY = { en: '| ANUM 360 Abu Dhabi', ar: '| ANUM 360 أبوظبي' };

const withSuffix = (head, suffix, max = TITLE_MAX) => {
  const room = max - suffix.length - 1;
  const text = squash(head);
  if (!text) return clampText(suffix.replace(/^\|\s*/, ''), max);
  return `${clampText(text, room)} ${suffix}`;
};

/**
 * `<title>` for a page: "{text} | ANUM 360 Gallery", ≤ 60 characters. A
 * title that already names the store is only clamped.
 */
export const pageTitle = (text, { max = TITLE_MAX } = {}) => {
  const value = squash(text);
  if (/ANUM\s*360/i.test(value)) return clampText(value, max);
  return withSuffix(value, SUFFIX.en, max);
};

/** "{Product} by {Brand} | ANUM 360 Abu Dhabi" (AR: "{المنتج} من {العلامة} | ANUM 360 أبوظبي"). */
export const productTitle = ({ name, brand, lang = 'en' } = {}) => {
  const ar = lang === 'ar';
  const product = squash(name) || (ar ? 'قطعة فاخرة' : 'Luxury piece');
  const maker = squash(brand);
  const suffix = ar ? SUFFIX_CITY.ar : SUFFIX_CITY.en;
  const joiner = ar ? ' من ' : ' by ';
  const room = TITLE_MAX - suffix.length - 1;
  if (!maker) return withSuffix(product, suffix);
  const byline = `${joiner}${maker}`;
  if (product.length + byline.length <= room) return `${product}${byline} ${suffix}`;
  // Keep the brand when there is room for a useful part of the name.
  if (room - byline.length >= 16) return `${clampText(product, room - byline.length)}${byline} ${suffix}`;
  return withSuffix(product, suffix);
};

/**
 * Meta description for a product, ≤ 155 characters: the start of the
 * description, then "Authentic {brand} at ANUM 360 Gallery, delivered
 * across the UAE." (Arabic in AR).
 */
export const productDescription = ({ name, brand, description, lang = 'en' } = {}) => {
  const ar = lang === 'ar';
  const maker = squash(brand);
  const tail = ar
    ? `${maker ? `${maker} أصلية` : 'قطع فاخرة أصلية'} من ANUM 360 Gallery، مع التوصيل في جميع أنحاء الإمارات.`
    : `Authentic ${maker || 'luxury'} at ANUM 360 Gallery, delivered across the UAE.`;
  const body = squash(String(description ?? '').replace(/<[^>]+>/g, ' ')) || squash(name);
  const room = DESCRIPTION_MAX - tail.length - 1;
  if (!body || room < 20) return clampText(tail, DESCRIPTION_MAX);
  return `${clampText(body, room)} ${tail}`;
};

/** Any meta description, ≤ 155 characters. */
export const metaDescription = (text) => clampText(text, DESCRIPTION_MAX);

// Query parameters that make a different page (everything else, such as
// tracking, sort and filter parameters, is dropped from the canonical).
const CANONICAL_PARAMS = {
  '/products': ['cat', 'page'],
};

/**
 * Absolute, self-referencing canonical URL for `pathname` + `search`: only
 * the parameters that make a different page are kept (the category of
 * /products, a page number above 1) plus `lang=ar` for the Arabic version.
 */
export const canonicalUrl = (pathname = '/', search = '', { lang } = {}) => {
  const path = `/${String(pathname || '/').replace(/^\/+/, '')}`.replace(/(.)\/+$/, '$1');
  const params = new URLSearchParams(search || '');
  const out = new URLSearchParams();
  (CANONICAL_PARAMS[path] || []).forEach((key) => {
    const value = params.get(key);
    if (!value) return;
    if (key === 'page') {
      const n = Number(value);
      if (Number.isInteger(n) && n > 1) out.set(key, String(n));
      return;
    }
    out.set(key, value);
  });
  const language = lang || params.get(LANG_PARAM);
  if (language === 'ar') out.set(LANG_PARAM, 'ar');
  const query = out.toString();
  return `${SITE_URL}${path}${query ? `?${query}` : ''}`;
};

/** The English (default) URL of a canonical URL: `lang` removed. */
export const defaultLanguageUrl = (url) => {
  const parsed = new URL(url, SITE_URL);
  parsed.searchParams.delete(LANG_PARAM);
  const query = parsed.searchParams.toString();
  return `${parsed.origin}${parsed.pathname}${query ? `?${query}` : ''}`;
};

/** The Arabic URL of a canonical URL: `lang=ar` added last. */
export const arabicUrl = (url) => {
  const parsed = new URL(url, SITE_URL);
  parsed.searchParams.delete(LANG_PARAM);
  parsed.searchParams.set(LANG_PARAM, 'ar');
  return `${parsed.origin}${parsed.pathname}?${parsed.searchParams.toString()}`;
};

/** hreflang alternates (en-AE, ar-AE, x-default) for a canonical URL. */
export const hreflangAlternates = (url) => {
  const en = defaultLanguageUrl(url);
  return [
    { hrefLang: 'en-AE', href: en },
    { hrefLang: 'ar-AE', href: arabicUrl(en) },
    { hrefLang: 'x-default', href: en },
  ];
};

/** "en" | "ar" from a query string's `lang`, else null. */
export const languageFromSearch = (search) => {
  try {
    const value = new URLSearchParams(search || '').get(LANG_PARAM);
    return value === 'ar' || value === 'en' ? value : null;
  } catch {
    return null;
  }
};

/**
 * Alt text for a product photo: "{name} by {brand}, {colour}" (AR:
 * "{name} من {brand}، {colour}"). The colour only when the piece comes in
 * exactly one, so the text never names a colour the photo may not show.
 */
export const productImageAlt = ({ name, brand, colors, color, lang = 'en' } = {}) => {
  const ar = lang === 'ar';
  const product = squash(name);
  const maker = squash(brand);
  const list = (Array.isArray(colors) ? colors : []).map(squash).filter(Boolean);
  const colour = squash(color) || (list.length === 1 ? list[0] : '');
  let text = product;
  if (maker && !product.toLowerCase().includes(maker.toLowerCase())) text += `${ar ? ' من ' : ' by '}${maker}`;
  if (colour) text += `${ar ? '، ' : ', '}${colour}`;
  return text.trim();
};

/** Brand page slug, like backend utils/catalog.brand_slug ("Maison Noir" → "maison-noir"). */
export const brandSlug = (name) => String(name || '')
  .toLowerCase()
  .trim()
  .replace(/[^\p{L}\p{N}_\s-]/gu, '')
  .replace(/[\s_-]+/g, '-')
  .replace(/^-+|-+$/g, '');
